import { clip, type Clip } from '../clip.ts';
import { TAU, inCubic, inOut, outCubic, outExpo, outQuad, phase, settle } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';
import { sentinelClips } from './sentinel.ts';

/*
 * Bruni, the Hollow Forger (models/bruni.ts, docs/ANTIHEROES.md): built on the fallen sentinel's bones, so he walks,
 * reels, staggers and falls as the sentinel does. His own: the cannon's shot (attack: he braces, leans into it and
 * the recoil throws his shoulders back at 0.13 s), the riveted bolt (a bigger recoil, the gauntlet steadying the
 * barrel), the black mortar (hammer on gauntlet, then both arms thrown up to send the shells high), the channel of
 * the forge turned (the hammer raised, the gauntlet reaching toward the towers, shaking) and the forge itself (the
 * hammer brought down on the ground, 0.18 s).
 *
 * Conventions as in sentinel.ts: −X on an arm swings it forward and up, −X on a forearm bends the elbow, +X on the
 * trunk leans it forward, +Y turns the trunk to its left.
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'flapF' | 'flapB'
  | 'armL' | 'foreL' | 'shield' | 'armR' | 'foreR' | 'sword'
  | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** Braced to fire: feet apart, leaning in, the right shoulder (the cannon's) turned forward. */
const BRACED: P = {
  root: [0, -0.04, 0, 0, -0.03, 0],
  body: [0.1, 0.12, 0],
  chest: [0.12, 0.2, 0],
  head: [0.05, -0.2, 0],
  armL: [-0.6, 0, 0.2],
  foreL: [-0.6, 0, 0],
  legL: [-0.3, 0, -0.08],
  shinL: [0.3, 0, 0],
  legR: [0.3, 0, 0.08],
  shinR: [0.25, 0, 0],
};
/** The recoil: shoulders thrown back, the head snapped up. */
const RECOIL: P = {
  ...BRACED,
  root: [0, -0.02, -0.06, 0, -0.03, 0],
  body: [-0.08, 0.05, 0],
  chest: [-0.2, 0.1, 0],
  head: [-0.15, -0.1, 0],
  flapF: [0.25, 0, 0],
  flapB: [-0.2, 0, 0],
};

const shot = (name: string, dur: number, big: number, label: string): Clip<B> =>
  clip<B>(name, dur, false, (b, t) => {
    const back: P = { ...RECOIL, chest: [-0.2 * big, 0.1, 0], body: [-0.08 * big, 0.05, 0] };
    timeline(b, t, [
      [0, BRACED],
      [0.1, BRACED],
      [0.13, back, outExpo],
      [0.45, BRACED, outCubic],
      [dur, {}, inOut],
    ], { head: 0.04, flapF: 0.08, flapB: 0.08 });
    b.root.pos(0, 0, -0.05 * big * Math.max(0, kick(t, 0.13, 5, 8)));
  }, (t) => thud(t, [[0.13, 0.03 * big]]), label);

/** The black mortar: the hammer rings on the gauntlet, then both arms thrown up. */
const mortar = (): Clip<B> =>
  clip<B>('mortar', 1.5, false, (b, t) => {
    const strike: P = { body: [0.1, 0, 0], chest: [0.2, 0.15, 0], armR: [-1.9, 0, -0.3], foreR: [-0.5, 0, 0], sword: [0.3, 0, 0], armL: [-1.0, 0, 0.4], foreL: [-0.9, 0, 0], legL: [-0.2, 0, 0], legR: [0.2, 0, 0] };
    const rung: P = { ...strike, armR: [-0.9, 0, 0.3], foreR: [-0.9, 0, 0], sword: [0.9, 0, 0] };
    const up: P = { body: [-0.12, 0, 0], chest: [-0.25, 0, 0], head: [-0.4, 0, 0], armR: [-2.8, 0, -0.3], armL: [-2.8, 0, 0.3], foreR: [-0.1, 0, 0], foreL: [-0.1, 0, 0], legL: [-0.2, 0, -0.06], legR: [0.2, 0, 0.06] };
    timeline(b, t, [
      [0, {}],
      [0.2, strike, outQuad],
      [0.32, rung, inCubic],
      [0.6, up, outExpo],
      [1.0, up],
      [1.5, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
  }, (t) => thud(t, [[0.32, 0.03]]), 'Mortero negro');

/** The forge turned, channelled: the hammer raised high, the gauntlet reaching out, shaking. */
const channel = (): Clip<B> =>
  clip<B>('channel', 1.2, true, (b, t) => {
    const p = phase(t, 1.2) * TAU;
    const shake = Math.sin(p * 7) * 0.03;
    b.root.pos(0, -0.04 + 0.01 * Math.sin(p * 2), 0).rot(-0.05, 0, 0);
    b.body.rot(-0.08, 0, 0);
    b.chest.rot(-0.18 + shake, -0.15, 0);
    b.head.rot(-0.2, 0.15, 0);
    b.armR.rot(-2.9 + shake, 0, -0.2);
    b.foreR.rot(-0.15);
    b.sword.rot(-0.2 + shake * 2);
    b.armL.rot(-1.45 + shake, 0, 0.15);
    b.foreL.rot(-0.05);
    b.shield.rot(0.2 * Math.sin(p * 5), 0, 0);
    b.legL.rot(-0.25, 0, -0.1);
    b.shinL.rot(0.3);
    b.legR.rot(0.25, 0, 0.1);
    b.shinR.rot(0.35);
    b.flapF.rot(0.1 * Math.sin(p * 3));
    b.flapB.rot(-0.12 + 0.1 * Math.sin(p * 3 + 1));
  }, (t) => {
    const k = Math.sin(phase(t, 1.2) * TAU * 7) * 0.006;
    return [1 + k, 1 - k, 1 + k];
  }, 'Canaliza');

/** The forge turned lands: the hammer from overhead onto the ground before him. */
const forge = (): Clip<B> =>
  clip<B>('forge', 1.6, false, (b, t) => {
    const high: P = { chest: [-0.18, -0.15, 0], head: [-0.2, 0.15, 0], armR: [-2.9, 0, -0.2], foreR: [-0.15, 0, 0], sword: [-0.2, 0, 0], armL: [-1.45, 0, 0.15], legL: [-0.25, 0, -0.1], shinL: [0.3, 0, 0], legR: [0.25, 0, 0.1], shinR: [0.35, 0, 0] };
    const down: P = { root: [0, 0, 0, 0, -0.16, 0.16], body: [0.35, 0, 0], chest: [0.45, 0, 0], head: [0.15, 0, 0], armR: [-0.8, 0, 0.1], foreR: [-0.1, 0, 0], sword: [0.9, 0, 0], armL: [0.3, 0, 0.35], legL: [-0.75, 0, -0.05], shinL: [0.95, 0, 0], legR: [0.5, 0, 0.05], shinR: [0.55, 0, 0], flapF: [-0.5, 0, 0], flapB: [0.45, 0, 0] };
    timeline(b, t, [
      [0, high],
      [0.18, down, inCubic],
      [0.8, down],
      [1.6, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
    b.root.pos(0.02 * kick(t, 0.18, 7, 6), 0, 0);
  }, (t) => thud(t, [[0.18, 0.09]]), 'La forja vuelta');

/** Clips by library kind (see ../rigs.ts): the sentinel's, with his own shot and powers in place of the bash. */
export const bruniClips = (): Clip[] =>
  [...sentinelClips().filter((c) => c.name !== 'attack'), shot('attack', 1.1, 1, 'Cañonazo'), shot('rivet', 1.3, 1.8, 'Perno remachado'), mortar(), channel(), forge()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { bruni: bruniClips };
